/**
 * Anchors: where a side conversation branched from, as text, not as DOM.
 *
 * A tab hangs off a span of an answer (or of the book). The span is kept as
 * the quoted words plus a little of what comes before and after them (a
 * text-quote selector, as Hypothesis and document comments do), never as
 * character offsets into markdown: the rendered DOM of an answer (code
 * blocks, KaTeX) does not line up with its source, and the book can be
 * republished. The quote is found again by searching the rendered text and
 * mapped back onto text nodes when it has to be shown or scrolled to.
 *
 * Highlights use the CSS Custom Highlight API where the browser has it (no
 * DOM is rewritten under React); elsewhere the chips under a message carry
 * the same information, so nothing depends on the highlight being drawn.
 */

export const EDGE_CHARS = 60;      // of prefix and suffix kept
export const AROUND_CHARS = 600;   // of the text around the quote, for the tutor
export const MIN_QUOTE = 2;
export const MAX_QUOTE = 1000;

const squash = (s) => (s || "").replace(/\s+/g, " ");

/** Every text node under `root`, in document order, with its start offset in the squashed text. */
/** KaTeX renders each formula three times (MathML, its LaTeX source, HTML); only the visible HTML counts. */
const hiddenMath = (node) => !!node.parentElement?.closest?.(".katex-mathml, annotation, [aria-hidden='true'] .katex-mathml");

function textNodes(root) {
  const out = [];
  const walker = document.createTreeWalker(root, window.NodeFilter.SHOW_TEXT,
    { acceptNode: (node) => (hiddenMath(node) ? window.NodeFilter.FILTER_REJECT : window.NodeFilter.FILTER_ACCEPT) });
  let offset = 0;
  let node = walker.nextNode();
  while (node) {
    const text = node.nodeValue || "";
    out.push({ node, start: offset, text });
    offset += text.length;
    node = walker.nextNode();
  }
  return out;
}

/** The text of `root` as one string, and a way back from an offset in it to (node, offset). */
function textIndex(root) {
  const nodes = textNodes(root);
  const full = nodes.map((n) => n.text).join("");
  const locate = (offset) => {
    for (let k = 0; k < nodes.length; k += 1) {
      const n = nodes[k];
      if (offset < n.start + n.text.length || k === nodes.length - 1) {
        return { node: n.node, offset: Math.max(0, Math.min(offset - n.start, n.text.length)) };
      }
    }
    return null;
  };
  return { nodes, full, locate };
}

/** The offset of `node`+`offset` (a Range boundary) in the joined text of `root`. */
function offsetIn(index, node, offset) {
  if (node.nodeType === 3) {
    const hit = index.nodes.find((n) => n.node === node);
    return hit ? hit.start + offset : null;
  }
  // An element boundary: the position of its `offset`-th child, or its end.
  const child = node.childNodes[offset];
  if (child) {
    const first = index.nodes.find((n) => child === n.node || child.contains(n.node));
    if (first) return first.start;
  }
  const inside = index.nodes.filter((n) => node.contains(n.node));
  if (inside.length) {
    const last = inside[inside.length - 1];
    return last.start + last.text.length;
  }
  return null;
}

/**
 * The selection inside `root` as an anchor: { quote, prefix, suffix, around },
 * or null when it is not a usable selection of `root`'s text.
 */
export function describeRange(range, root) {
  if (!range || !root || range.collapsed) return null;
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const index = textIndex(root);
  const a = offsetIn(index, range.startContainer, range.startOffset);
  const b = offsetIn(index, range.endContainer, range.endOffset);
  if (a === null || b === null || b <= a) return null;
  const quote = squash(index.full.slice(a, b)).trim();
  if (quote.length < MIN_QUOTE || quote.length > MAX_QUOTE) return null;
  const prefix = squash(index.full.slice(Math.max(0, a - EDGE_CHARS), a));
  const suffix = squash(index.full.slice(b, b + EDGE_CHARS));
  const half = Math.max(0, Math.floor((AROUND_CHARS - quote.length) / 2));
  const around = squash(index.full.slice(Math.max(0, a - half), b + half)).trim();
  return { quote, prefix, suffix, around };
}

/** The current window selection, if it lies within `root`. */
export function describeSelection(root) {
  const sel = window.getSelection?.();
  if (!sel || sel.isCollapsed || !sel.rangeCount) return null;
  return describeRange(sel.getRangeAt(0), root);
}

/** How well the text around a candidate position matches the anchor's prefix and suffix. */
function edgeScore(full, at, end, prefix, suffix) {
  let score = 0;
  const before = squash(full.slice(Math.max(0, at - EDGE_CHARS), at));
  const after = squash(full.slice(end, end + EDGE_CHARS));
  if (prefix) {
    for (let k = 1; k <= Math.min(prefix.length, before.length); k += 1) {
      if (prefix[prefix.length - k] !== before[before.length - k]) break;
      score += 1;
    }
  }
  if (suffix) {
    for (let k = 0; k < Math.min(suffix.length, after.length); k += 1) {
      if (suffix[k] !== after[k]) break;
      score += 1;
    }
  }
  return score;
}

/**
 * Find an anchor's quote in `root` again. Returns a Range, or null when the
 * words are no longer there. Among several occurrences, the one whose
 * surroundings match the prefix and suffix best wins.
 */
export function findQuote(root, anchor) {
  if (!root || !anchor?.quote) return null;
  const index = textIndex(root);
  const full = index.full;
  // Whitespace in the DOM may differ from the squashed quote: match on a
  // squashed copy while remembering where each squashed character came from.
  const map = [];
  let squashed = "";
  let lastSpace = false;
  for (let k = 0; k < full.length; k += 1) {
    const ch = full[k];
    if (/\s/.test(ch)) {
      if (lastSpace) continue;
      lastSpace = true;
      squashed += " ";
    } else {
      lastSpace = false;
      squashed += ch;
    }
    map.push(k);
  }
  const quote = squash(anchor.quote).trim();
  if (!quote) return null;
  const hits = [];
  let from = 0;
  for (;;) {
    const at = squashed.indexOf(quote, from);
    if (at < 0) break;
    hits.push(at);
    from = at + 1;
    if (hits.length > 50) break;
  }
  if (!hits.length) return null;
  let best = hits[0];
  if (hits.length > 1) {
    let bestScore = -1;
    for (const at of hits) {
      const score = edgeScore(squashed, at, at + quote.length, anchor.prefix, anchor.suffix);
      if (score > bestScore) { bestScore = score; best = at; }
    }
  }
  const startFull = map[best];
  const endFull = map[best + quote.length - 1] + 1;
  const start = index.locate(startFull);
  const end = index.locate(endFull);
  if (!start || !end) return null;
  try {
    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  } catch { return null; }
}

/** The browser can draw highlights without touching the DOM. */
export const canHighlight = () => typeof window !== "undefined" && !!window.CSS?.highlights && typeof window.Highlight === "function";

const STYLE_ID = "tutor-highlight-style";
function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    ::highlight(tutor-threads) { background-color: rgba(1,114,166,0.16); text-decoration: underline dotted rgba(1,114,166,0.8); }
    ::highlight(tutor-flash) { background-color: rgba(245,158,11,0.45); }
  `;
  document.head.appendChild(style);
}

const registry = new Map();   // name → Map(owner → Range[])

/**
 * Draw `ranges` under highlight `name` on behalf of `owner` (a component
 * instance); replaces what that owner drew before. Other owners' ranges in
 * the same highlight are kept. No-op where the API is missing.
 */
export function setHighlights(name, owner, ranges) {
  if (!canHighlight()) return;
  ensureStyle();
  let owners = registry.get(name);
  if (!owners) { owners = new Map(); registry.set(name, owners); }
  if (ranges && ranges.length) owners.set(owner, ranges);
  else owners.delete(owner);
  const all = [...owners.values()].flat();
  if (!all.length) { window.CSS.highlights.delete(name); return; }
  window.CSS.highlights.set(name, new window.Highlight(...all));
}

/** Flash a range for a moment (after scrolling to it). */
export function flashRange(range, ms = 2200) {
  if (!range) return;
  const owner = {};
  setHighlights("tutor-flash", owner, [range]);
  setTimeout(() => setHighlights("tutor-flash", owner, []), ms);
}
